import Header from '../../components/Header';
import React, { FC, useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  TextField,
  Tabs,
  Tab,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { tokens } from '../../theme';
import moment from 'moment';
import { FilterContext } from '../../contexts/FilterContext';
import SnapshotDecreasesTable from '../../components/vnr/SnapshotDecreasesTable';
import RecordHistoryModal from '../../components/vnr/RecordHistoryModal';
import SnapshotDateComparison from '../../components/vnr/SnapshotDateComparison';
import fetchData from '../../helpers/fetchData';

interface Network {
  name: string;
}

interface Theme {
  name: string;
}

interface AvailableDate {
  snapshot_date: string;
  total_changes: number;
  total_decreases: number;
  total_decrease_amount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const SnapshotEvaluationPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;

  const [networks, setNetworks] = useState<Network[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [originalDates, setOriginalDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  } | null>(null);

  // Local state for this page
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [leadStartDate, setLeadStartDate] = useState<Date | null>(null);
  const [leadEndDate, setLeadEndDate] = useState<Date | null>(null);
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [selectedPlanungsbereich, setSelectedPlanungsbereich] = useState<string[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<string>('all');
  const [minChange, setMinChange] = useState<number>(0);
  const [tabValue, setTabValue] = useState<number>(0);
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Default date range: last 7 days
  const defaultStartDate = useMemo(() => moment().subtract(7, 'days').toDate(), []);
  const defaultEndDate = useMemo(() => moment().toDate(), []);

  // Initialize dates and store originals
  useEffect(() => {
    const originalStartDate = filterState.startDate;
    const originalEndDate = filterState.endDate;
    setOriginalDates({
      startDate: originalStartDate,
      endDate: originalEndDate,
    });

    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
  }, []);

  // Cleanup effect to restore FilterContext on unmount
  useEffect(() => {
    return () => {
      if (originalDates) {
        setFilterState((prev) => ({
          ...prev,
          startDate: originalDates.startDate,
          endDate: originalDates.endDate,
        }));
      }
    };
  }, [originalDates, setFilterState]);

  // Fetch available networks
  useEffect(() => {
    fetch('/api/vnr/networks')
      .then((response) => response.json())
      .then((data) => {
        setNetworks(data);
        const allNetworkNames = data.map((n: Network) => n.name);
        setSelectedNetworks(allNetworkNames);
      })
      .catch((error) => console.error('Error fetching networks:', error));
  }, []);

  // Fetch available themes based on selected networks
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const params = new URLSearchParams();

        if (selectedNetworks.length > 0) {
          selectedNetworks.forEach((network) => params.append('network', network));
        }

        if (startDate && endDate) {
          params.append('start_date', startDate.toISOString().split('T')[0]);
          params.append('end_date', endDate.toISOString().split('T')[0]);
        }

        const response = await fetch(`/api/vnr/themes?${params.toString()}`);
        const data = await response.json();
        setThemes(data);
      } catch (error) {
        console.error('Error fetching themes:', error);
      }
    };

    fetchThemes();
  }, [selectedNetworks, startDate, endDate]);

  // Fetch available snapshot dates
  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        const dates = (await fetchData<AvailableDate[]>(
          '/api/vnr/snapshots/available-dates',
          {},
        )) as AvailableDate[];
        setAvailableDates(dates);
      } catch (error) {
        console.error('Error fetching available dates:', error);
      }
    };

    fetchAvailableDates();
  }, []);

  const handleNetworkChange = (event: any) => {
    const value = event.target.value;
    setSelectedNetworks(typeof value === 'string' ? value.split(',') : value);
  };

  const handleThemeChange = (event: any) => {
    const value = event.target.value;
    setSelectedThemes(typeof value === 'string' ? value.split(',') : value);
  };

  const handlePlanungsbereichChange = (event: any) => {
    const value = event.target.value;
    setSelectedPlanungsbereich(typeof value === 'string' ? value.split(',') : value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRowClick = (row: any) => {
    setSelectedRecord(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  const planungsbereichOptions = ['GeVestor', 'Investor'];
  const columnOptions = [
    { value: 'all', label: 'Alle Spalten' },
    { value: 'abrechnungswert', label: 'Revenue' },
    { value: 'abrechnungswert_30', label: 'Revenue (30d)' },
    { value: 'anfo_bestaetigt_30', label: 'ANFOs (30d)' },
    { value: 'anfo_bestaetigt_ohne_v', label: 'ANFOs (ohne V)' },
  ];

  const earliestDate =
    availableDates.length > 0 ? availableDates[availableDates.length - 1].snapshot_date : 'N/A';
  const latestDate = availableDates.length > 0 ? availableDates[0].snapshot_date : 'N/A';

  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex">
        <Header
          title="VNR Lead Snapshots"
          subtitle="Evaluation negativer Wertänderungen in VNR Lead Data"
        />
      </Box>

      {/* Available Snapshots Info */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Verfügbare Snapshots
                </Typography>
                <Typography variant="h4">{availableDates.length}</Typography>
                <Typography variant="body2">
                  Von {earliestDate} bis {latestDate}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Letzte Wertminderungen
                </Typography>
                <Typography variant="h4" color="error">
                  {availableDates.length > 0 ? availableDates[0].total_decreases : 0}
                </Typography>
                <Typography variant="body2">am {latestDate}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Letzte Wertminderungen Summe
                </Typography>
                <Typography variant="h4" color="error">
                  {availableDates.length > 0
                    ? parseFloat(String(availableDates[0].total_decrease_amount || 0)).toFixed(2) +
                      '€'
                    : '0€'}
                </Typography>
                <Typography variant="body2">am {latestDate}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filter Controls */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          label="Snapshot Start"
          type="date"
          value={startDate ? moment(startDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setStartDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Snapshot End"
          type="date"
          value={endDate ? moment(endDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setEndDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <TextField
          label="Lead Start (optional)"
          type="date"
          value={leadStartDate ? moment(leadStartDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setLeadStartDate(e.target.value ? new Date(e.target.value) : null)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="Lead End (optional)"
          type="date"
          value={leadEndDate ? moment(leadEndDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setLeadEndDate(e.target.value ? new Date(e.target.value) : null)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="network-select-label">Netzwerk</InputLabel>
          <Select
            labelId="network-select-label"
            multiple
            value={selectedNetworks}
            onChange={handleNetworkChange}
            input={<OutlinedInput label="Netzwerk" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {networks.map((network) => (
              <MenuItem key={network.name} value={network.name}>
                {network.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="theme-select-label">Thema</InputLabel>
          <Select
            labelId="theme-select-label"
            multiple
            value={selectedThemes}
            onChange={handleThemeChange}
            input={<OutlinedInput label="Thema" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {themes.map((theme) => (
              <MenuItem key={theme.name} value={theme.name}>
                {theme.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="planungsbereich-select-label">Planungsbereich</InputLabel>
          <Select
            labelId="planungsbereich-select-label"
            multiple
            value={selectedPlanungsbereich}
            onChange={handlePlanungsbereichChange}
            input={<OutlinedInput label="Planungsbereich" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {planungsbereichOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="column-select-label">Spalte</InputLabel>
          <Select
            labelId="column-select-label"
            value={selectedColumn}
            onChange={(e) => setSelectedColumn(e.target.value)}
            label="Spalte"
          >
            {columnOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Min. Änderung (€)"
          type="number"
          value={minChange}
          onChange={(e) => setMinChange(parseFloat(e.target.value) || 0)}
          sx={{ minWidth: 150 }}
        />
      </Box>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Aktuelle Wertminderungen" />
          <Tab label="Zwei Daten Vergleich" />
        </Tabs>
      </Box>

      {/* Tab 1: Recent consecutive changes */}
      <TabPanel value={tabValue} index={0}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography>
            <strong>Hinweis:</strong> Diese Ansicht zeigt alle negativen Wertänderungen im
            ausgewählten Zeitraum (Standard: letzte 7 Tage). Klicken Sie auf eine Zeile, um die
            vollständige Änderungshistorie für diesen Lead Record zu sehen.
          </Typography>
        </Alert>

        <SnapshotDecreasesTable
          startDate={startDate}
          endDate={endDate}
          network={selectedNetworks.length > 0 ? selectedNetworks : undefined}
          theme={selectedThemes.length > 0 ? selectedThemes : undefined}
          planungsbereich={selectedPlanungsbereich.length > 0 ? selectedPlanungsbereich : undefined}
          column={selectedColumn}
          minChange={minChange}
          leadStartDate={leadStartDate}
          leadEndDate={leadEndDate}
          onRowClick={handleRowClick}
        />
      </TabPanel>

      {/* Tab 2: Compare two specific dates */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography>
            <strong>Hinweis:</strong> Wählen Sie zwei Snapshot-Daten aus, um direkt zu vergleichen,
            welche Werte sich zwischen diesen beiden Zeitpunkten geändert haben.
          </Typography>
        </Alert>

        <SnapshotDateComparison
          network={selectedNetworks.length > 0 ? selectedNetworks : undefined}
          theme={selectedThemes.length > 0 ? selectedThemes : undefined}
          planungsbereich={selectedPlanungsbereich.length > 0 ? selectedPlanungsbereich : undefined}
          column={selectedColumn}
          minChange={minChange}
          leadStartDate={leadStartDate}
          leadEndDate={leadEndDate}
          availableDates={availableDates.map((d) => d.snapshot_date)}
        />
      </TabPanel>

      {/* Record History Modal */}
      {selectedRecord && (
        <RecordHistoryModal
          open={modalOpen}
          onClose={handleModalClose}
          datum={selectedRecord.datum}
          eAffiliateId={selectedRecord.e_affiliate_id}
          anmeldungen={selectedRecord.anmeldungen}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </Box>
  );
};

export default SnapshotEvaluationPage;
